import { CSSProperties } from "react";
import {
  CategoryTopicsType,
  CategoryType,
  OrderUnitsType,
  TopicsType,
} from "../../../../../types/Terminal/TerminalTypes";
import terminalTicketIconActiveLarge from "../../../../../assets/icons/terminalTicketIconActiveLarge.svg";
import {
  getMediaBackgroundSize,
  normalizeMediaUrl,
} from "../../../../../utils/mediaBackground";
import {
  TerminalAppearanceResponse,
  TerminalBackgroundMedia,
} from "../../../../../types/Admin/TerminalAppearanceTypes";
import { ResolvedChildrenModeVisualSettings } from "../../../../../utils/Terminal/childrenModeThemes";

export type languageType = "en" | "by" | "ru";

export type TerminalCategoryNode = Omit<
  CategoryTopicsType,
  "id" | "subCategories" | "isActiveNow"
> & {
  id: number | string;
  isActiveNow?: boolean;
  subCategories?: TerminalCategoryNode[];
};

export type TerminalTicketItem = TopicsType | TerminalCategoryNode;

export type TerminalCSSVariables = React.CSSProperties & {
  [key: `--terminal-${string}`]: string | number | undefined;
  [key: `--ticker-${string}`]: string | number | undefined;
};

export const elementsLocalization = {
  returnBtn: {
    ru: "Вернуться назад",
    by: "Вярнуцца назад",
    en: "Go back",
  },
  title: {
    ru: "Добро пожаловать, дорогой клиент!",
    by: "Сардэчна запрашаем, дарагі кліент!",
    en: "Welcome, dear customer!",
  },
  subTitle: {
    ru: "Пожалуйста, выберите услугу:",
    by: "Калі ласка, абярыце паслугу:",
    en: "Please select a service:",
  },
};

export const fontFamilyMap: Record<string, string> = {
  SFPro: "var(--font-SFPro)",
  Inter: "var(--font-inter)",
  Montserrat: "Montserrat",
  "Roboto-400": "var(--font-roboto-400)",
  Roboto: "var(--font-roboto-bold)",
  Bebas: "var(--font-bebas)",
};

export const fontWeightMap: Record<string, number> = {
  regular: 400,
  medium: 500,
  bold: 700,
};

export const getVideoMimeType = (url: string): string | undefined => {
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (cleanUrl.endsWith(".mp4")) return "video/mp4";
  if (cleanUrl.endsWith(".webm")) return "video/webm";
  if (cleanUrl.endsWith(".ogv") || cleanUrl.endsWith(".ogg"))
    return "video/ogg";
  if (cleanUrl.endsWith(".mkv")) return "video/x-matroska";
  return undefined;
};

export const getMediaObjectFit = (fitMode: string | null | undefined) => {
  switch (fitMode) {
    case "contain":
      return "contain";
    case "cover":
      return "cover";
    case "stretch":
      return "fill";
    case "none":
      return "none";
    case "width":
    case "height":
    case "scale":
    default:
      return "contain";
  }
};

export function transformOrderUnitsToArr(
  data: OrderUnitsType,
): TerminalTicketItem[] {
  const topics = data.categoryView.topics || [];
  const categories = data.categoryView.categories || [];
  const processCategory = (
    category: CategoryType,
    prefix = "",
  ): TerminalCategoryNode => {
    const categoryId = `${prefix}${category.id}`;
    let subCategories: TerminalCategoryNode[] = [];
    if (category.subCategories && category.subCategories.length > 0) {
      subCategories = category.subCategories.map((subCategory) => {
        return processCategory(subCategory, `${categoryId}.`);
      });
    }
    return {
      ...category,
      id: categoryId,
      subCategories: subCategories,
    };
  };

  const processedCategories = categories.map((category) =>
    processCategory(category),
  );

  return [...topics, ...processedCategories];
}

export function checkIsCategory(
  categoryOrTopic: TerminalTicketItem,
): categoryOrTopic is TerminalCategoryNode {
  return Boolean(
    (categoryOrTopic as TerminalCategoryNode).subCategories?.length ||
      (categoryOrTopic as TerminalCategoryNode).topics?.length,
  );
}

export const getTicketName = (
  ticket: TerminalTicketItem,
  language: languageType,
): string => {
  switch (language) {
    case "ru":
      return ticket.nameRu;
    case "en":
      return ticket.nameEn;
    case "by":
      return ticket.nameBe;
    default:
      return ticket.nameRu;
  }
};

export const getNormalTicketCircleStyle = (
  ticket: TerminalTicketItem,
  isCategory: boolean,
): CSSProperties | undefined => {
  if (!ticket.iconUrl) return undefined;

  const normalizedUrl = normalizeMediaUrl(ticket.iconUrl);
  const fitMode = ticket.iconFitMode ?? "scale";
  const scale = ticket.iconScale ?? 100;
  const positionX = ticket.iconPositionX ?? 50;
  const positionY = ticket.iconPositionY ?? 50;
  const backgroundSize = getMediaBackgroundSize(fitMode, scale);

  if (isCategory) {
    return {
      ["--terminal-section-outer-circle-image" as string]: `url(${normalizedUrl})`,
      ["--terminal-section-outer-circle-size" as string]: backgroundSize,
      ["--terminal-section-outer-circle-position-x" as string]: `${positionX}%`,
      ["--terminal-section-outer-circle-position-y" as string]: `${positionY}%`,
    } as CSSProperties;
  }

  return {
    ["--terminal-ticket-outer-circle-small-image" as string]: `url(${normalizedUrl})`,
    ["--terminal-ticket-outer-circle-small-size" as string]: backgroundSize,
    ["--terminal-ticket-outer-circle-small-position-x" as string]: `${positionX}%`,
    ["--terminal-ticket-outer-circle-small-position-y" as string]: `${positionY}%`,
  } as CSSProperties;
};

export const getChildrenTicketCircleStyle = (
  ticket: TerminalTicketItem,
  isCategory: boolean,
  useStandardIcons: boolean,
  hideIcons = false,
): CSSProperties | undefined => {
  if (hideIcons) return undefined;

  const iconUrl = useStandardIcons ? ticket.iconUrl : ticket.childrenIconUrl;
  if (!iconUrl) return undefined;

  const normalizedUrl = normalizeMediaUrl(iconUrl);
  const fitMode = useStandardIcons
    ? (ticket.iconFitMode ?? "scale")
    : (ticket.childrenIconFitMode ?? "scale");
  const scale = useStandardIcons
    ? (ticket.iconScale ?? 100)
    : (ticket.childrenIconScale ?? 100);
  const positionX = useStandardIcons
    ? (ticket.iconPositionX ?? 50)
    : (ticket.childrenIconPositionX ?? 50);
  const positionY = useStandardIcons
    ? (ticket.iconPositionY ?? 50)
    : (ticket.childrenIconPositionY ?? 50);
  const backgroundSize = getMediaBackgroundSize(fitMode, scale);

  if (isCategory) {
    return {
      ["--terminal-section-outer-circle-image" as string]: `url(${normalizedUrl})`,
      ["--terminal-section-outer-circle-size" as string]: backgroundSize,
      ["--terminal-section-outer-circle-position-x" as string]: `${positionX}%`,
      ["--terminal-section-outer-circle-position-y" as string]: `${positionY}%`,
    } as CSSProperties;
  }

  return {
    ["--terminal-ticket-outer-circle-small-image" as string]: `url(${normalizedUrl})`,
    ["--terminal-ticket-outer-circle-small-size" as string]: backgroundSize,
    ["--terminal-ticket-outer-circle-small-position-x" as string]: `${positionX}%`,
    ["--terminal-ticket-outer-circle-small-position-y" as string]: `${positionY}%`,
  } as CSSProperties;
};

export type ChildrenModeIconMeta = {
  src: string;
  objectFit: "contain" | "cover" | "fill";
  objectPosition: string;
  scale: number;
};

export const getChildrenModeIconMeta = (
  ticket: TerminalTicketItem,
  useStandardIcons: boolean,
  hideIcons = false,
): ChildrenModeIconMeta | null => {
  if (hideIcons) return null;

  const iconUrl = useStandardIcons ? ticket.iconUrl : ticket.childrenIconUrl;
  if (!iconUrl) {
    return null;
  }

  const fitMode = useStandardIcons
    ? (ticket.iconFitMode ?? "scale")
    : (ticket.childrenIconFitMode ?? "scale");
  const scale = useStandardIcons
    ? (ticket.iconScale ?? 100)
    : (ticket.childrenIconScale ?? 100);
  const positionX = useStandardIcons
    ? (ticket.iconPositionX ?? 50)
    : (ticket.childrenIconPositionX ?? 50);
  const positionY = useStandardIcons
    ? (ticket.iconPositionY ?? 50)
    : (ticket.childrenIconPositionY ?? 50);

  const objectFit: "contain" | "cover" | "fill" =
    fitMode === "cover"
      ? "cover"
      : fitMode === "stretch"
        ? "fill"
        : "contain";

  return {
    src: normalizeMediaUrl(iconUrl),
    objectFit,
    objectPosition: `${positionX}% ${positionY}%`,
    scale: Number.isFinite(scale) ? (scale as number) : 100,
  };
};

export const resolveNormalBackground = (
  appearance?: TerminalAppearanceResponse,
): TerminalBackgroundMedia | null => {
  const backgroundList = appearance?.backgrounds;
  const backgroundId = appearance?.settings?.activeBackgroundId;

  if (!backgroundList?.length) {
    return null;
  }

  if (backgroundId) {
    return (
      backgroundList.find((background) => background.id === backgroundId) ||
      null
    );
  }

  return backgroundList.find((background) => background.isActive) || null;
};

export const resolveChildrenBackground = (
  appearance: TerminalAppearanceResponse | undefined,
  childrenVisual: ResolvedChildrenModeVisualSettings,
): TerminalBackgroundMedia | null => {
  const backgroundList = appearance?.childrenBackgrounds;
  const backgroundId = childrenVisual.activeBackgroundId;

  if (backgroundList?.length) {
    if (backgroundId) {
      return (
        backgroundList.find((background) => background.id === backgroundId) ||
        null
      );
    }

    const fileNameMatch = childrenVisual.backgroundFileNameMatch?.trim();
    if (fileNameMatch) {
      const match = fileNameMatch.toLowerCase();
      const found =
        backgroundList.find((background) =>
          (background.fileName ?? "").toLowerCase().includes(match),
        ) || null;
      if (found) return found;
    }
  }

  if (childrenVisual.localBackgroundUrl) {
    return {
      id: -1,
      fileName:
        childrenVisual.localBackgroundUrl.split("/").pop() || "children-bg",
      mediaType: "IMAGE",
      isActive: true,
      url: childrenVisual.localBackgroundUrl,
    };
  }

  return null;
};

const buildAppearanceStyleBase = (
  appearance?: TerminalAppearanceResponse,
): TerminalCSSVariables => {
  const settings = appearance?.settings;
  if (!settings) return {};
  const sectionButtons = settings.sectionButtons ?? settings.buttons;
  const sectionGrid = settings.sectionGrid ?? settings.grid;
  const gridMinWidth = settings.grid?.minCardWidth;
  const gridMaxWidth = settings.grid?.maxCardWidth;
  const normalizedMinWidth =
    gridMinWidth !== undefined && gridMaxWidth !== undefined
      ? Math.min(gridMinWidth, gridMaxWidth)
      : gridMinWidth;
  const normalizedMaxWidth =
    gridMaxWidth !== undefined && normalizedMinWidth !== undefined
      ? Math.max(gridMaxWidth, normalizedMinWidth)
      : gridMaxWidth;
  const sectionGridMinWidth = sectionGrid?.minCardWidth;
  const sectionGridMaxWidth = sectionGrid?.maxCardWidth;
  const normalizedSectionMinWidth =
    sectionGridMinWidth !== undefined && sectionGridMaxWidth !== undefined
      ? Math.min(sectionGridMinWidth, sectionGridMaxWidth)
      : sectionGridMinWidth;
  const normalizedSectionMaxWidth =
    sectionGridMaxWidth !== undefined &&
    normalizedSectionMinWidth !== undefined
      ? Math.max(sectionGridMaxWidth, normalizedSectionMinWidth)
      : sectionGridMaxWidth;
  const outerCircleSmallScale = settings.buttons?.ticketOuterCircleSmallScale;
  const outerCircleSmallPositionX =
    settings.buttons?.ticketOuterCircleSmallPositionX;
  const outerCircleSmallPositionY =
    settings.buttons?.ticketOuterCircleSmallPositionY;
  const outerCircleSmallFitMode =
    settings.buttons?.ticketOuterCircleSmallFitMode ?? "scale";
  const outerCircleSmallImage =
    settings.buttons?.ticketOuterCircleSmallUrl ||
    terminalTicketIconActiveLarge;
  const sectionOuterCircleScale =
    sectionButtons?.ticketOuterCircleSmallScale ?? outerCircleSmallScale;
  const sectionOuterCirclePositionX =
    sectionButtons?.ticketOuterCircleSmallPositionX ??
    outerCircleSmallPositionX;
  const sectionOuterCirclePositionY =
    sectionButtons?.ticketOuterCircleSmallPositionY ??
    outerCircleSmallPositionY;
  const sectionOuterCircleFitMode =
    sectionButtons?.ticketOuterCircleSmallFitMode ?? outerCircleSmallFitMode;
  const sectionOuterCircleImage =
    sectionButtons?.ticketOuterCircleSmallUrl ||
    settings.buttons?.ticketOuterCircleSmallUrl ||
    terminalTicketIconActiveLarge;
  const tickerSpeedSeconds =
    appearance?.settings?.tickerSpeed &&
    Number.isFinite(appearance.settings.tickerSpeed)
      ? appearance.settings.tickerSpeed
      : 50;

  return {
    "--terminal-grid-columns": settings.grid?.columns,
    "--terminal-grid-gap": settings.grid?.gap
      ? `${settings.grid.gap}px`
      : undefined,
    "--terminal-grid-row-gap":
      settings.grid?.rowGap !== null && settings.grid?.rowGap !== undefined
        ? `${settings.grid.rowGap}px`
        : undefined,
    "--terminal-card-min-height": settings.grid?.minCardHeight
      ? `${settings.grid.minCardHeight}px`
      : undefined,
    "--terminal-card-max-height": settings.grid?.maxCardHeight
      ? `${settings.grid.maxCardHeight}px`
      : undefined,
    "--terminal-card-min-width":
      normalizedMinWidth !== undefined
        ? `${normalizedMinWidth}px`
        : undefined,
    "--terminal-card-max-width":
      normalizedMaxWidth !== undefined
        ? `${normalizedMaxWidth}px`
        : undefined,
    "--terminal-grid-padding-top": settings.grid?.paddingTop
      ? `${settings.grid.paddingTop}px`
      : undefined,
    "--terminal-grid-padding-x": settings.grid?.paddingX
      ? `${settings.grid.paddingX}px`
      : undefined,
    "--terminal-section-grid-columns": sectionGrid?.columns,
    "--terminal-section-grid-gap": sectionGrid?.gap
      ? `${sectionGrid.gap}px`
      : undefined,
    "--terminal-section-grid-row-gap":
      sectionGrid?.rowGap !== null && sectionGrid?.rowGap !== undefined
        ? `${sectionGrid.rowGap}px`
        : undefined,
    "--terminal-section-card-min-height": sectionGrid?.minCardHeight
      ? `${sectionGrid.minCardHeight}px`
      : undefined,
    "--terminal-section-card-max-height": sectionGrid?.maxCardHeight
      ? `${sectionGrid.maxCardHeight}px`
      : undefined,
    "--terminal-section-card-min-width":
      normalizedSectionMinWidth !== undefined
        ? `${normalizedSectionMinWidth}px`
        : undefined,
    "--terminal-section-card-max-width":
      normalizedSectionMaxWidth !== undefined
        ? `${normalizedSectionMaxWidth}px`
        : undefined,
    "--terminal-section-grid-padding-top": sectionGrid?.paddingTop
      ? `${sectionGrid.paddingTop}px`
      : undefined,
    "--terminal-section-grid-padding-x": sectionGrid?.paddingX
      ? `${sectionGrid.paddingX}px`
      : undefined,
    "--terminal-section-outer-circle-image": sectionOuterCircleImage
      ? `url(${sectionOuterCircleImage})`
      : undefined,
    "--terminal-section-outer-circle-size": getMediaBackgroundSize(
      sectionOuterCircleFitMode,
      sectionOuterCircleScale,
    ),
    "--terminal-section-outer-circle-scale":
      sectionOuterCircleScale !== null &&
      sectionOuterCircleScale !== undefined
        ? `${sectionOuterCircleScale}%`
        : undefined,
    "--terminal-section-outer-circle-position-x":
      sectionOuterCirclePositionX !== null &&
      sectionOuterCirclePositionX !== undefined
        ? `${sectionOuterCirclePositionX}%`
        : undefined,
    "--terminal-section-outer-circle-position-y":
      sectionOuterCirclePositionY !== null &&
      sectionOuterCirclePositionY !== undefined
        ? `${sectionOuterCirclePositionY}%`
        : undefined,
    "--terminal-ticket-outer-circle-small-image": outerCircleSmallImage
      ? `url(${outerCircleSmallImage})`
      : undefined,
    "--terminal-ticket-outer-circle-small-size": getMediaBackgroundSize(
      outerCircleSmallFitMode,
      outerCircleSmallScale,
    ),
    "--terminal-ticket-outer-circle-small-scale":
      outerCircleSmallScale !== null && outerCircleSmallScale !== undefined
        ? `${outerCircleSmallScale}%`
        : undefined,
    "--terminal-ticket-outer-circle-small-position-x":
      outerCircleSmallPositionX !== null &&
      outerCircleSmallPositionX !== undefined
        ? `${outerCircleSmallPositionX}%`
        : undefined,
    "--terminal-ticket-outer-circle-small-position-y":
      outerCircleSmallPositionY !== null &&
      outerCircleSmallPositionY !== undefined
        ? `${outerCircleSmallPositionY}%`
        : undefined,
    "--terminal-greeting-font-size":
      settings.greeting?.fontSize !== null &&
      settings.greeting?.fontSize !== undefined
        ? `${settings.greeting.fontSize}px`
        : undefined,
    "--terminal-greeting-margin-top":
      settings.greeting?.paddingTop !== null &&
      settings.greeting?.paddingTop !== undefined
        ? `${settings.greeting.paddingTop}px`
        : undefined,
    "--terminal-greeting-margin-bottom":
      settings.greeting?.paddingBottom !== null &&
      settings.greeting?.paddingBottom !== undefined
        ? `${settings.greeting.paddingBottom}px`
        : undefined,
    "--terminal-greeting-text-color": settings.greeting?.textColor,
    "--terminal-header-font-family":
      fontFamilyMap[settings.header?.fontFamily] || "var(--font-SFPro)",
    "--terminal-header-font-size":
      settings.header?.fontSize !== null &&
      settings.header?.fontSize !== undefined
        ? `${settings.header.fontSize}px`
        : undefined,
    "--terminal-card-padding-top": settings.buttons?.hideOuterCircleSmall
      ? "60px"
      : undefined,
    "--terminal-section-card-padding-top":
      sectionButtons?.hideOuterCircleSmall ? "60px" : undefined,
    "--ticker-animation-duration": `${tickerSpeedSeconds}s`,
  };
};

export const buildNormalAppearanceStyle = (
  appearance?: TerminalAppearanceResponse,
): TerminalCSSVariables => {
  const settings = appearance?.settings;
  if (!settings) return {};
  const sectionButtons = settings.sectionButtons ?? settings.buttons;

  return {
    ...buildAppearanceStyleBase(appearance),
    "--terminal-ticket-gradient": settings.buttons?.gradient,
    "--terminal-ticket-text-color": settings.buttons?.textColor,
    "--terminal-ticket-font-family":
      fontFamilyMap[settings.buttons?.fontFamily] || "var(--font-SFPro)",
    "--terminal-ticket-font-weight":
      fontWeightMap[settings.buttons?.fontWeight] || 600,
    "--terminal-ticket-font-size":
      settings.buttons?.fontSize !== null &&
      settings.buttons?.fontSize !== undefined
        ? `${settings.buttons.fontSize}px`
        : undefined,
    "--terminal-section-gradient": sectionButtons?.gradient,
    "--terminal-section-text-color": sectionButtons?.textColor,
    "--terminal-section-font-family":
      fontFamilyMap[sectionButtons?.fontFamily] ||
      fontFamilyMap[settings.buttons?.fontFamily] ||
      "var(--font-SFPro)",
    "--terminal-section-font-weight":
      fontWeightMap[sectionButtons?.fontWeight] ||
      fontWeightMap[settings.buttons?.fontWeight] ||
      600,
    "--terminal-section-font-size":
      sectionButtons?.fontSize !== null &&
      sectionButtons?.fontSize !== undefined
        ? `${sectionButtons.fontSize}px`
        : settings.buttons?.fontSize !== null &&
            settings.buttons?.fontSize !== undefined
          ? `${settings.buttons.fontSize}px`
          : undefined,
    "--terminal-headerBG": settings.header?.backgroundColor,
    "--terminal-headerText": settings.header?.textColor,
  };
};

export const buildChildrenAppearanceStyle = (
  appearance: TerminalAppearanceResponse | undefined,
  childrenVisual: ResolvedChildrenModeVisualSettings,
): TerminalCSSVariables => {
  const settings = appearance?.settings;
  if (!settings) return {};
  const sectionButtons = settings.sectionButtons ?? settings.buttons;

  return {
    ...buildAppearanceStyleBase(appearance),
    "--terminal-ticket-gradient": settings.buttons?.gradient,
    "--terminal-ticket-text-color": childrenVisual.buttonTextColor,
    "--terminal-ticket-font-family":
      fontFamilyMap[childrenVisual.buttonFontFamily ?? ""] ||
      "var(--font-SFPro)",
    "--terminal-ticket-font-weight":
      fontWeightMap[settings.buttons?.fontWeight] || 600,
    "--terminal-ticket-font-size":
      childrenVisual.serviceButtonFontSize !== undefined
        ? `${childrenVisual.serviceButtonFontSize}px`
        : undefined,
    "--terminal-section-gradient": sectionButtons?.gradient,
    "--terminal-section-text-color": childrenVisual.buttonTextColor,
    "--terminal-section-font-family":
      fontFamilyMap[childrenVisual.buttonFontFamily ?? ""] ||
      fontFamilyMap[sectionButtons?.fontFamily] ||
      "var(--font-SFPro)",
    "--terminal-section-font-weight":
      fontWeightMap[sectionButtons?.fontWeight] ||
      fontWeightMap[settings.buttons?.fontWeight] ||
      600,
    "--terminal-section-font-size":
      childrenVisual.sectionButtonFontSize !== undefined
        ? `${childrenVisual.sectionButtonFontSize}px`
        : undefined,
    "--terminal-headerBG": childrenVisual.headerBackgroundColor,
    "--terminal-headerText": childrenVisual.headerTextColor,
  };
};
